import { ZfStack, ZfTypography } from '@zafire/react';
import type { CSSProperties, FunctionComponent, KeyboardEvent, MouseEvent } from 'react';
import { useRef } from 'react';

import { useElementHovering } from '../../../../../family-tree';
import { useShadowRootCustomStyles } from '../../../../hooks/useShadowRootCustomStyles';
import type { IncomeExpensesBarLegendEntry } from './IncomeExpensesBarTypes';

const swatchStyle: CSSProperties = {
    width: 12,
    height: 12,
    flexShrink: 0,
    borderRadius: '50%',
};

export type IncomeExpensesBarLegendToggleProps = {
    entry: IncomeExpensesBarLegendEntry;
    hidden: boolean;
    onToggleSeries: (seriesIndex: number) => void;
};

export const IncomeExpensesBarLegendToggle: FunctionComponent<IncomeExpensesBarLegendToggleProps> = ({ entry, hidden, onToggleSeries }) => {
    const legendRef = useRef<HTMLZfStackElement>(null);
    const labelRef = useRef<HTMLZfTypographyElement>(null);

    const isHovering = useElementHovering(legendRef);

    useShadowRootCustomStyles(labelRef, 'label { cursor: pointer !important; }');

    const onClick = (e: MouseEvent) => {
        e.preventDefault();
        onToggleSeries(entry.seriesIndex);
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleSeries(entry.seriesIndex);
        }
    };

    return (
        <ZfStack ref={legendRef} direction="row" spacing="extra-small" alignment="center" onClick={onClick} onKeyDown={onKeyDown} style={{ cursor: 'pointer' }}>
            <span style={{ ...swatchStyle, backgroundColor: entry.color }} />
            <ZfTypography
                ref={labelRef}
                variant="label-large"
                style={{
                    color: isHovering ? 'var(--fg-color-default-emphasis)' : 'var(--fg-color-default-normal)',
                    textDecoration: hidden ? 'line-through' : undefined,
                    opacity: hidden ? 0.7 : 1,
                    cursor: 'pointer',
                }}
            >
                {entry.name}
            </ZfTypography>
        </ZfStack>
    );
};
